import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Link } from 'react-router-dom';
import { DashboardLayout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { useTrial } from '../contexts/TrialContext';
import api from '../lib/api';
import WhatsAppConnectCard from '../components/WhatsAppConnectCard';
import ActivityCard from '../components/ActivityCard';
import { CreditsCard, UnlocksCard } from '../components/CreditsCard';
import ExamHistoryTable from '../components/ExamHistoryTable';
import NextStepsHero from '../components/nextsteps/NextStepsHero';
import FocusDim from '../components/nextsteps/FocusDim';
import { useNextSteps } from '../lib/nextSteps';
import { NextClassCard } from '../components/events/NextClassCard';
import { useDashboardWidgets } from '../lib/dashboardWidgets';
import { useIsMobile } from '../lib/useIsMobile';
import { useNextClass } from '../lib/nextClass';
import { FileText, Activity, Target, Flame, Award, Crown, ClipboardList, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';

const DashboardCharts = lazy(() => import('../components/dashboard/DashboardCharts'));

const fadeUp = { hidden: { opacity: 0, y: 16 }, show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] } } };
const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.06 } } };

/** Entrance animation on desktop only — phones render plain blocks (no layout-thrashing transforms while loading). */
function Block({ mobile, className, children }) {
  if (mobile) return <div className={className}>{children}</div>;
  return <motion.div variants={fadeUp} className={className}>{children}</motion.div>;
}

export default function Dashboard() {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [performance, setPerformance] = useState(null);
  const [scoreHistory, setScoreHistory] = useState([]);
  const [plan, setPlan] = useState(null);
  const isPro = user?.subscription_status && user.subscription_status !== 'free';
  const { entryLabel } = useTrial();
  const ns = useNextSteps();
  const widgets = useDashboardWidgets();
  const upcomingEvent = useNextClass(user?.id, !!widgets.next_class_card);
  const nsActive = !!(ns.data && ns.data.steps && ns.data.steps.length > 0);
  const nsClassId = ns.data?.class?.event?.id;
  const heroReady = !ns.loading;

  const loadScores = () => api.get('/api/dashboard/scores').then(r => setScoreHistory(r.data || [])).catch(() => {});
  // Secondary data waits for the hero (next step) so the first paint on phones is one request, not twelve.
  useEffect(() => {
    if (!heroReady) return;
    loadScores();
    if (!isMobile) api.get('/api/performance').then(r => setPerformance(r.data)).catch(() => {});
  }, [heroReady, isMobile]);
  useEffect(() => {
    if (!widgets.welcome_panel) return;
    api.get('/api/study-plan').then(r => setPlan(r.data)).catch(() => {});
  }, [isPro, widgets.welcome_panel]);

  const planUnlocked = !!isPro || (plan && plan.status !== 'locked');
  const planBtn = !planUnlocked ? null : !plan || plan.status === 'intake' || plan.status === 'locked'
    ? { label: 'Create My Study Plan', icon: <ClipboardList size={15} />, testid: 'create-plan-btn' }
    : plan.status === 'generating' ? { label: 'My Study Plan · in progress', icon: <Loader2 size={15} className="animate-spin" />, testid: 'plan-progress-btn' }
      : { label: 'Open My Study Plan', icon: <ClipboardList size={15} />, testid: 'open-plan-btn' };

  const Wrap = isMobile ? 'div' : motion.div;
  const wrapProps = isMobile ? {} : { initial: 'hidden', animate: 'show', variants: stagger };

  return (
    <DashboardLayout>
      <Wrap className="space-y-4 md:space-y-6" data-testid="dashboard-page" data-mobile={isMobile ? '1' : '0'} {...wrapProps}>
        {/* Guided next steps — the one thing to do now (class card → mock test → …) */}
        {nsActive && <Block mobile={isMobile}><NextStepsHero data={ns.data} onChanged={ns.refresh} /></Block>}
        <FocusDim active={nsActive && !!ns.data.dim} uid={user?.id}>
        {/* Welcome Banner (Admin → Student Dashboard switch; off by default) */}
        {widgets.welcome_panel && (
        <Block mobile={isMobile} className="bg-gradient-to-r from-[#0F172A] to-[#1E293B] rounded-2xl p-5 md:p-8 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10" style={{ background: 'radial-gradient(circle at 80% 50%, #2563EB, transparent 60%)' }} />
          <div className="relative flex items-center justify-between flex-wrap gap-4" data-testid="dashboard-welcome-panel">
            <div>
              <h1 className="font-[Manrope] text-xl md:text-2xl font-extrabold text-white tracking-tight mb-1">
                Welcome back, {user?.name || 'Student'}
              </h1>
              <p className="text-slate-400 text-sm">Continue your CELPIP preparation journey</p>
            </div>
            {planBtn && (
              <Link to="/study-plan" className="flex items-center gap-2 bg-[#2563EB] text-white rounded-full px-6 py-2.5 font-semibold text-sm shadow-lg shadow-blue-500/20 hover:bg-[#1D4ED8] transition-all hover:-translate-y-0.5" data-testid={planBtn.testid}>
                {planBtn.icon}{planBtn.label}
              </Link>
            )}
          </div>
        </Block>
        )}

        {/* Credits ("money in your account") — free students with a balance */}
        {!isPro && widgets.credits_card && <Block mobile={isMobile}><CreditsCard /></Block>}
        {!isPro && widgets.unlocks_card && <Block mobile={isMobile}><UnlocksCard /></Block>}

        {/* Next Group Class — the one running RIGHT NOW is highlighted red with "Join now" (hidden when the next-steps block already shows this class) */}
        {widgets.next_class_card && upcomingEvent && upcomingEvent.id !== nsClassId && (
          <Block mobile={isMobile}><NextClassCard ev={upcomingEvent} /></Block>
        )}

        {/* Exam History — every completed test + teacher-feedback status */}
        {scoreHistory.length > 0 && (
          <Block mobile={isMobile}>
            <ExamHistoryTable rows={scoreHistory} isPro={!!isPro || ['admin', 'teacher', 'contributor', 'sales'].includes(user?.role)} onChanged={loadScores} />
          </Block>
        )}

        {/* Desktop only: stats, charts, activity, WhatsApp — phones keep to the essentials */}
        {!isMobile && (
          <>
            <Block mobile={false} className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {[
                { icon: <FileText size={16} />, label: 'Exams Taken', value: performance?.total_exams || 0, tint: 'bg-blue-50 text-blue-600' },
                { icon: <Activity size={16} />, label: 'Questions Done', value: performance?.total_questions_answered || 0, tint: 'bg-purple-50 text-purple-600' },
                { icon: <Award size={16} />, label: 'Best Score', value: performance?.best_overall ? `CLB ${performance.best_overall}` : '—', tint: 'bg-emerald-50 text-emerald-600' },
                { icon: <Target size={16} />, label: 'Target CLB', value: performance?.target_clb || 9, tint: 'bg-rose-50 text-rose-600' },
                { icon: <Flame size={16} />, label: 'Study Streak', value: `${performance?.study_streak || 0} day${(performance?.study_streak || 0) === 1 ? '' : 's'}`, tint: 'bg-amber-50 text-amber-600' },
                { icon: <Crown size={16} />, label: 'Plan', value: isPro ? 'Pro' : entryLabel, tint: isPro ? 'bg-amber-50 text-amber-600' : 'bg-slate-100 text-slate-500' },
              ].map((s, i) => (
                <div key={i} className="bg-white rounded-2xl border border-slate-100 p-4 shadow-[0_2px_8px_rgba(0,0,0,0.04)] card-hover" data-testid={`dashboard-stat-${s.label.toLowerCase().replace(/ /g, '-')}`}>
                  <div className={`w-9 h-9 rounded-xl ${s.tint} flex items-center justify-center mb-2.5`}>{s.icon}</div>
                  <div className="text-lg font-extrabold font-[Manrope] text-slate-900 leading-none truncate">{s.value}</div>
                  <p className="text-[11px] font-medium text-slate-400 mt-1.5">{s.label}</p>
                </div>
              ))}
            </Block>

            {performance && performance.total_exams > 0 && (
              <Block mobile={false}>
                <Suspense fallback={<div className="h-[240px]" />}><DashboardCharts performance={performance} /></Suspense>
              </Block>
            )}

            <Block mobile={false}><ActivityCard /></Block>
            <Block mobile={false}><WhatsAppConnectCard /></Block>
          </>
        )}
        </FocusDim>
      </Wrap>
    </DashboardLayout>
  );
}
